import { lazy, Suspense, useEffect, useState, type ReactNode } from "react";

// Lazy-load notification primitives that aren't needed for FCP/LCP.
// They pull in Radix Toast + sonner — significant JS that would otherwise
// sit in the initial bundle even though nothing renders on first paint.
const Toaster = lazy(() => import("@/components/ui/toaster").then((m) => ({ default: m.Toaster })));
const Sonner = lazy(() => import("@/components/ui/sonner").then((m) => ({ default: m.Toaster })));

interface DeferredUIProps {
  children: ReactNode;
}

/**
 * Mounts the toasters after the browser is idle so they don't block FCP/LCP.
 *
 * IMPORTANT: they render as SIBLINGS of `children`, not as parents. Both
 * Toaster and Sonner render into portals, so they don't need to wrap the app
 * tree. This keeps the `children` slot's parent element type stable across
 * the ready→not-ready transition, avoiding a full app-tree unmount/remount
 * (which would swallow the first tap and replay hero animations).
 *
 * Radix Tooltip works without a TooltipProvider (uses default delays), so
 * we skip mounting one here — dropping it removes another parent element that
 * would otherwise wrap `children` and cause a remount when it appeared.
 */
const DeferredUI = ({ children }: DeferredUIProps) => {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    if (typeof w.requestIdleCallback === "function") {
      const id = w.requestIdleCallback(() => setReady(true), { timeout: 2000 });
      return () => {
        if (w.cancelIdleCallback) w.cancelIdleCallback(id);
      };
    }
    const t = window.setTimeout(() => setReady(true), 1500);
    return () => window.clearTimeout(t);
  }, []);

  return (
    <>
      {children}
      {ready && (
        <Suspense fallback={null}>
          <Toaster />
          <Sonner />
        </Suspense>
      )}
    </>
  );
};

export default DeferredUI;
